import { useCallback, useEffect, useMemo, useState } from 'react';
import BottomSheet from '../common/BottomSheet/BottomSheet';
import ButtonIconGhostMedium from '../common/ButtonIconGhost/ButtonIconGhostMedium';
import { Icon } from '../icon/icon';
import ButtonIconSolidMedium from '../common/ButtonIconSolid/ButtonIconSolidMedium';
import ButtonIconSolidLarge from '../common/ButtonIconSolid/ButtonIconSolidLarge';
import { Form, Tooltip } from 'antd';
import InputComponent from '../common/InputComponent/InputComponent';
import Table from '../common/Table/Table';
import { Column, ColumnGroup } from '../common/Table/Table';
import { roundTo } from '../../utils/geometry/epsilon';
import { useTranslation } from 'react-i18next';
import { getValueFromString } from '../../utils/data/getValue.utils';
import ButtonPrimaryLarge from '../common/ButtonPrimary/ButtonPrimaryLarge';
import { useTransitionCalculations } from '../../hooks/useTransitionCalculations';
import { transformRegistryData } from '../../utils/data/transitionDataTransform';
import TabComponent from '../common/TabComponent/TabComponent';
import { useSelector } from 'react-redux';
import { walkLeaves } from '../../utils/tree/walkLeaves';
import { cn } from '../../utils/string/cn.utils';
import createSafeEvaluator from '../../utils/eval/createSafeEvaluator';

const Transition = ({
  open,
  onClose,
  id = 'estimated-cost-container',
  sceneManager,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [zeroedRows, setZeroedRows] = useState(new Set());

  const onToggleZeroRow = useCallback(name => {
    setZeroedRows(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);
  const country_code = process.env.REACT_APP_COUNTRY_CODE || 'vn';

  const defaultConfig = useSelector(state => state.defaultConfig);

  const flatProfitConfig = useMemo(() => {
    return [
      ...walkLeaves(
        defaultConfig[country_code + 'Config'].profitConfig.data?.pro ??
          defaultConfig[country_code + 'Config'].profitConfig.data ??
          {}
      ),
    ];
  }, [defaultConfig]);

  const flatRuleConfig = useMemo(() => {
    return [
      ...walkLeaves(
        defaultConfig[country_code + 'Config']?.ruleConfig?.data?.pro ??
          defaultConfig[country_code + 'Config']?.ruleConfig?.data ??
          {}
      ),
    ];
  }, [defaultConfig]);

  const reloadData = useCallback(async () => {
    const values = form.getFieldsValue();
    const country_code = process.env.REACT_APP_COUNTRY_CODE;
    const result = await sceneManager?.getResultEstimatePrice?.();

    const registryCost = result?.registryCost?.[country_code];
    const aggregatedTotals = result?.aggregatedTotals?.[country_code];
    const aggregatedSaleTotals = result?.aggregatedSaleTotals?.[country_code];

    const walkLeavesRegistryCost = [...walkLeaves(registryCost ?? {})];
    const flatRegistryCost = walkLeavesRegistryCost
      .map(item => {
        const configProfit = flatProfitConfig.find(
          p => p.key === item.key
        )?.value;
        const baseFormulaWithProfit =
          flatRuleConfig.find(
            rule => rule.key === `${item.key}.baseFormulaWithProfit`
          )?.value ?? '';

        values[item.key] ??= configProfit;
        const profit = values[item.key];
        return {
          name: item.key,
          items: item.value,
          note: '',
          profit,
          baseFormulaWithProfit,
          cost: item.value?.reduce((prev, curr) => {
            return prev + Number(curr.cost);
          }, 0),
          sale: item.value?.reduce((prev, curr) => {
            return prev + Number(curr.sale);
          }, 0),
        };
      })
      .filter(item => item.cost || item.sale);

    if (values['transport.importDuty'] == null) {
      values['transport.importDuty'] = flatRuleConfig.find(
        item => item.key === 'variables.importDuty'
      )?.value;
    }
    if (values['exchangeRate'] == null) {
      values['exchangeRate'] = flatRuleConfig.find(
        item => item.key === 'variables.exchangeRate'
      )?.value;
    }
    if (values['GST'] == null) {
      values['GST'] = (() => {
        const value =
          flatRuleConfig.find(item => item.key === 'variables.gst')?.value ?? 0;
        return Number(value) * 100;
      })();
    }

    values['grandCost'] = aggregatedTotals?.grand;
    values['grand'] = aggregatedSaleTotals?.grand;
    values['grandSpecialTexture'] = 0; // bỏ giảm giá ván
    //   result?.total?.totalMaterials?.costBoards *
    //   (result?.useSpecialTexture ? 0.05 : 0);
    form.setFieldsValue(values);

    setData(flatRegistryCost);
  }, [sceneManager, form, flatProfitConfig, flatRuleConfig]);

  useEffect(() => {
    if (!open) return;
    reloadData();
  }, [open]);

  return (
    <BottomSheet
      onClose={onClose}
      classNames={{
        title: 'flex justify-between self-stretch items-center px-24 py-12',
        body: `!p-24 !pb-0`,
      }}
      title={
        <>
          <h1 className="text-headline-small">Chi tiết báo giá</h1>
          <div className="flex justify-end items-center self-stretch gap-16">
            <ButtonPrimaryLarge
              title={'Cập nhật dữ liệu'}
              icon={<Icon.ICRotateLeft />}
              onClick={() => reloadData()}
            />
            <ButtonIconGhostMedium icon={<Icon.ICClose />} onClick={onClose} />
          </div>
        </>
      }
      open={open}
    >
      <Form form={form} layout="horizontal" className="h-full w-full">
        <TabComponent
          className="[&_.ant-tabs-content-holder]:overflow-auto h-full"
          items={[
            {
              key: 'national',
              label: 'Báo giá trong nước',
              children: (
                <RenderSheetNationality
                  data={data}
                  id={id}
                  form={form}
                  zeroedRows={zeroedRows}
                  onToggleZeroRow={onToggleZeroRow}
                />
              ),
            },
            {
              key: 'international',
              label: 'Báo giá nước ngoài',
              disable: country_code === 'vn',
              children: (
                <RenderSheetInternational
                  data={data}
                  form={form}
                  zeroedRows={zeroedRows}
                  onToggleZeroRow={onToggleZeroRow}
                />
              ),
            },
          ]}
        />
      </Form>
    </BottomSheet>
  );
};

export default Transition;

const RenderSheetNationality = ({
  data,
  id,
  form,
  zeroedRows = new Set(),
  onToggleZeroRow,
}) => {
  const { t } = useTranslation();
  const { calculateSaleNational } = useTransitionCalculations(form);
  const dataNational = useMemo(
    () =>
      data
        .filter(
          item =>
            !['importDuty', 'shippingFee', 'groundTripFee'].some(key =>
              item.name?.includes(key)
            )
        )
        .map(item =>
          zeroedRows.has(item.name)
            ? {
                ...item,
                cost: 0,
                sale: 0,
                items: item.items.map(i => ({ ...i, cost: 0, sale: 0 })),
              }
            : item
        ),
    [data, zeroedRows]
  );

  const dataWood = useMemo(
    () => dataNational?.filter(registry => !registry.name.includes('special')),
    [dataNational]
  );
  const dataSpecial = useMemo(
    () => dataNational?.filter(registry => registry.name.includes('special')),
    [dataNational]
  );

  const grand = useMemo(() => {
    return calculateSaleNational(dataNational);
  }, [dataNational]);
  return (
    <div className="flex overflow-auto h-full w-full flex-col">
      <div id={id} className="flex w-full flex-col"></div>
      {open && (
        <div className="flex items-center self-stretch px-24 py-48 justify-end gap-12 flex-col">
          <Table
            dataSource={[...dataNational]}
            columns={[
              {
                title: 'Tên',
                dataIndex: 'name',
                key: 'name',
                width: 200,
                render: value => {
                  return (
                    <div className="flex py-10 ps-16 pe-24 items-center gap-4 self-stretch">
                      <span className="text-body-medium">{value}</span>
                    </div>
                  );
                },
              },
              {
                title: 'TSLN',
                dataIndex: 'name',
                key: 'tsln',
                width: 100,
                render: value => {
                  return (
                    <Form.Item name={value} className="mb-0">
                      <InputComponent type="number" />
                    </Form.Item>
                  );
                },
              },
              {
                title: 'Giá vốn',
                dataIndex: 'items',
                key: 'items',
                width: 500,
                // minWidth: 136,
                render: (value, record) => {
                  return (
                    <div className="flex py-10 ps-16 pe-24 items-start gap-8 self-stretch flex-col">
                      <div className="flex flex-col items-start gap-0">
                        {value
                          .reduce((prev, curr) => {
                            const items = [...prev];
                            const index = items.findIndex(
                              item =>
                                item.quantity === curr.quantity &&
                                item.unitPrice === curr.unitPrice &&
                                item.name === curr.name
                            );
                            if (index !== -1) {
                              items[index].count += 1;
                              items[index].cost += curr.cost;
                            } else {
                              items.push({ ...curr, count: 1 });
                            }
                            return items;
                          }, [])
                          .sort((a, b) => b.quantity - a.quantity)
                          .map((item, index, arr) => {
                            return (
                              <>
                                <div className="flex items-center gap-4 ml-16 text-black-high">
                                  {!!item.name && (
                                    <span className="text-label-large text-black-low">
                                      ({t(getValueFromString(item.name))})
                                    </span>
                                  )}
                                  {!!item.count && item.count > 1 && (
                                    <>
                                      <span className="text-label-large">
                                        {item.count}
                                      </span>
                                      *
                                    </>
                                  )}
                                  <span className="text-label-large">
                                    {item.quantity}
                                  </span>
                                  *
                                  <span className="text-label-large">
                                    {Number(item.unitPrice).toLocaleString(
                                      'vi-VN'
                                    )}
                                    <span className="text-black-low">
                                      /{item.unit}
                                    </span>
                                  </span>
                                  =
                                  <span className="text-body-small-emphasized text-black-low">
                                    {roundTo(item.cost, 0)?.toLocaleString(
                                      'vi-VN',
                                      {
                                        style: 'currency',
                                        currency: 'VND',
                                      }
                                    )}
                                  </span>
                                </div>
                              </>
                            );
                          })}
                      </div>
                      <div className="flex items-center gap-6">
                        =
                        <Tooltip
                          title={
                            <span
                              className={cn(
                                'text-title-small-emphasized flex gap-12'
                              )}
                            >
                              {record.cost}≠
                              {roundTo(
                                value.reduce((prev, curr) => {
                                  return prev + Number(curr.cost);
                                }, 0),
                                0
                              )}
                            </span>
                          }
                        >
                          <span
                            className={cn(
                              'text-title-small-emphasized',
                              roundTo(record.cost, 0) !==
                                roundTo(
                                  value.reduce((prev, curr) => {
                                    return prev + Number(curr.cost);
                                  }, 0),
                                  0
                                ) && 'text-red-600'
                            )}
                          >
                            {record.cost?.toLocaleString('vi-VN', {
                              style: 'currency',
                              currency: 'VND',
                            })}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  );
                },
              },
              {
                title: 'Giá bán (Đã thêm TSLN nếu có)',
                dataIndex: 'saleItems',
                key: 'saleItems',
                width: 500,
                render: (_, record) => {
                  const cost = roundTo(record.cost, 0);
                  const profit = record.profit;

                  const expr = String(record.baseFormulaWithProfit || '');
                  const formula = expr
                    .replace(/cost/g, cost.toLocaleString('vi-VN'))
                    .replace(/\(1 - profit\)/g, 1 - profit);
                  const result = roundTo(
                    createSafeEvaluator(record.baseFormulaWithProfit, {
                      cost,
                      profit,
                    }),
                    0
                  );

                  return (
                    <div className="flex py-10 ps-16 pe-24 items-center gap-4 self-stretch">
                      <span>{formula}</span>
                      <span>=</span>
                      <span>{result.toLocaleString('vi-VN')}</span>
                    </div>
                  );
                },
              },
              {
                title: 'Ghi chú',
                dataIndex: '',
                key: '',
                render: (_, record) => {
                  return <div></div>;
                },
              },
              {
                title: '',
                dataIndex: 'name',
                key: 'action',
                width: 60,
                render: name => {
                  const isZeroed = zeroedRows.has(name);
                  return (
                    <ButtonIconGhostMedium
                      icon={
                        isZeroed ? <Icon.ICRotateLeft /> : <Icon.ICDelete />
                      }
                      onClick={() => onToggleZeroRow(name)}
                    />
                  );
                },
              },
            ]}
            pagination={false}
          />
          <Table
            showHeader={false}
            dataSource={[
              {
                name: 'Tổng giá phần gỗ',
                items: dataWood,
                saleItems: [],
                note: <div></div>,
              },
              {
                name: 'Tổng giá thiết bị, đá, kính, khối bo cong',
                items: dataSpecial,
                saleItems: [],
              },
            ]}
            columns={[
              {
                title: 'Tên',
                dataIndex: 'name',
                key: 'name',
                width: 200,
                render: value => {
                  return (
                    <div className="flex py-10 ps-16 pe-24 items-center gap-4 self-stretch">
                      <span className="text-body-medium">{value}</span>
                    </div>
                  );
                },
              },
              {
                title: 'TSLN',
                dataIndex: 'name',
                key: 'tsln',
                width: 100,
                render: value => {
                  return <div> </div>;
                },
              },
              {
                title: 'Giá vốn',
                dataIndex: 'items',
                key: 'items',
                width: 500,
                render: value => {
                  return (
                    <div className="flex py-10 ps-16 pe-24 items-start gap-8 self-stretch flex-col">
                      <div className="flex flex-col items-start gap-0">
                        {value.map((item, index, arr) => {
                          return (
                            <>
                              <div className="flex items-center gap-4 ml-16 text-black-high">
                                {!!item.name && (
                                  <span className="text-label-large text-black-low">
                                    ({t(getValueFromString(item.name))})
                                  </span>
                                )}

                                <span className="text-label-large">
                                  {roundTo(item.cost, 0)?.toLocaleString(
                                    'vi-VN',
                                    {
                                      style: 'currency',
                                      currency: 'VND',
                                    }
                                  )}
                                </span>
                              </div>
                            </>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-6">
                        =
                        <span className="text-title-small-emphasized">
                          {roundTo(
                            value.reduce((prev, curr) => prev + curr.cost, 0),
                            0
                          )?.toLocaleString('vi-VN', {
                            style: 'currency',
                            currency: 'VND',
                          })}
                        </span>
                      </div>
                    </div>
                  );
                },
              },
              {
                title: 'Giá bán (Đã thêm TSLN nếu có)',
                dataIndex: 'saleItems',
                key: 'saleItems',
                width: 500,
                render: (_, record) => {
                  const total = record.items
                    .map((item, index, arr) => {
                      const cost = roundTo(item.cost, 0);
                      const profit = item.profit;

                      const expr = String(item.baseFormulaWithProfit || '');
                      const formula = expr
                        .replace(/cost/g, cost)
                        .replace(/\(1-profit\)/g, 1 - profit);
                      const result = roundTo(
                        createSafeEvaluator(item.baseFormulaWithProfit, {
                          cost,
                          profit,
                        }),
                        0
                      );
                      return result;
                    })
                    .reduce((prev, curr) => prev + Number(curr), 0);
                  return (
                    <div className="flex py-10 ps-16 pe-24 items-start gap-8 self-stretch flex-col">
                      <div className="flex flex-col items-start gap-0">
                        {record.items.map((item, index, arr) => {
                          const cost = roundTo(item.cost, 0);
                          const profit = item.profit;

                          const expr = String(item.baseFormulaWithProfit || '');
                          const formula = expr
                            .replace(/cost/g, cost)
                            .replace(/\(1-profit\)/g, 1 - profit);
                          const result = roundTo(
                            createSafeEvaluator(item.baseFormulaWithProfit, {
                              cost,
                              profit,
                            }),
                            0
                          );

                          return (
                            <div className="flex items-center gap-4 ml-16 text-black-high">
                              {!!item.name && (
                                <span className="text-label-large text-black-low">
                                  ({t(getValueFromString(item.name))})
                                </span>
                              )}

                              <span className="text-label-large">
                                {roundTo(result, 0)?.toLocaleString('vi-VN', {
                                  style: 'currency',
                                  currency: 'VND',
                                })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-6">
                        {'='}
                        <span className="text-title-small-emphasized">
                          {total?.toLocaleString('vi-VN', {
                            style: 'currency',
                            currency: 'VND',
                          })}
                        </span>
                      </div>
                    </div>
                  );
                },
              },
              {
                title: 'Ghi chú',
                dataIndex: 'note',
                key: 'note',
                render: value => {
                  return (
                    <div className="flex py-10 ps-16 pe-24 items-center gap-4 self-stretch">
                      {value}
                    </div>
                  );
                },
              },
            ]}
            pagination={false}
          />
          <div className="flex justify-end self-stretch items-center">
            <span className="text-title-small-emphasized text-right">
              Tổng giá bán-Việt Nam
            </span>
            <span className="text-body-large-emphasized text-right w-[400px]">
              {roundTo(grand)?.toLocaleString('vi-VN', {
                style: 'currency',
                currency: 'VND',
              })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const RenderSheetInternational = ({
  data,
  form,
  zeroedRows = new Set(),
  onToggleZeroRow,
}) => {
  const {
    getFormValues,
    calculateSale,
    calculateCostNational,
    calculateSaleNational,
    // calculateSaleImportDutyNational,
    // calculateCostShippingFeeNational,
    // calculateSaleShippingFeeNational,
    // calculateCostGroundTripNational,
    // calculateSaleGroundTripNational,
    // calculateCostTotalNational,
    // calculateSaleTotalNational,
    calculateGST,
    calculateSaleTotalAfterGST,
    calculateProfit,
    calculatePercentProfit,
  } = useTransitionCalculations(form);

  const zeroItem = useCallback(
    item =>
      zeroedRows.has(item.name)
        ? {
            ...item,
            cost: 0,
            sale: 0,
            items: item.items.map(i => ({ ...i, cost: 0, sale: 0 })),
          }
        : item,
    [zeroedRows]
  );

  const dataNational = useMemo(
    () =>
      data
        .filter(
          item =>
            !['importDuty', 'shippingFee', 'groundTripFee'].some(key =>
              item.name?.includes(key)
            )
        )
        .map(zeroItem),
    [data, zeroItem]
  );

  const dataShipping = useMemo(
    () =>
      data
        .filter(item =>
          ['importDuty', 'shippingFee', 'groundTripFee'].some(key =>
            item.name?.includes(key)
          )
        )
        .map(zeroItem),
    [data, zeroItem]
  );

  const renderTotalNationalRow = useCallback(
    ({ exchangeRate }) => {
      return {
        name: (
          <span className="text-label-large-emphasized text-black-high">
            Tổng (Chi phí, giá bán trong nước)
          </span>
        ),
        costVND: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(calculateCostNational(dataNational), 0)?.toLocaleString()}
          </span>
        ),
        costForeignCurrency: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(
              calculateCostNational(dataNational) / exchangeRate,
              2
            ).toLocaleString()}
          </span>
        ),
        saleVND: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(calculateSaleNational(dataNational), 0)?.toLocaleString()}
          </span>
        ),
        saleForeignCurrency: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(
              calculateSaleNational(dataNational) / exchangeRate,
              2
            ).toLocaleString()}
          </span>
        ),
        note: (
          <div>
            {/* {grandSpecialTexture ? (
              <>
                <span className="text-body-medium text-black-low">
                  Giảm 5% giá ván có sẵn =
                </span>
                <span className="text-label-large-emphasized">
                  {grandSpecialTexture?.toLocaleString('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                  })}
                </span>
              </>
            ) : (
              'Không dùng ván có sẵn'
            )} */}
          </div>
        ),
      };
    },
    [calculateCostNational, calculateSaleNational, dataNational]
  );

  // const renderImportDutyRow = useCallback(
  //   ({ exchangeRate }) => {
  //     return {
  //       name: 'Import duty',
  //       tsln: (
  //         <Form.Item name={'importDuty'} className="mb-0">
  //           <InputComponent type="number" />
  //         </Form.Item>
  //       ),
  //       saleVND: calculateSaleImportDutyNational().toLocaleString(),
  //       saleForeignCurrency: roundTo(
  //         calculateSaleImportDutyNational() / exchangeRate,
  //         2
  //       ).toLocaleString(),
  //       note: '= Import duty * Tổng giá bán',
  //     };
  //   },
  //   [calculateSaleImportDutyNational]
  // );

  const renderShippingFeeRow = useCallback(
    ({ exchangeRate }) => {
      return (
        dataShipping?.map(item => {
          const costVND = roundTo(
            item.items?.reduce((prev, curr) => {
              return prev + curr.cost;
            }, 0),
            0
          );
          const saleVND = roundTo(
            item.items?.reduce((prev, curr) => {
              return prev + curr.sale;
            }, 0),
            0
          );
          return {
            name: item.name,
            tsln: (
              <Form.Item name={item.name} className="mb-0">
                <InputComponent type="number" />
              </Form.Item>
            ),
            costVND: costVND?.toLocaleString(),
            costForeignCurrency: roundTo(
              costVND / exchangeRate,
              2
            ).toLocaleString(),
            saleVND: saleVND?.toLocaleString(),
            saleForeignCurrency: roundTo(
              saleVND / exchangeRate,
              2
            ).toLocaleString(),
            // note: (
            //   <div>
            //     {grandSpecialTexture ? (
            //       <>
            //         <span className="text-body-medium text-black-low">
            //           Giảm 5% giá ván có sẵn =
            //         </span>
            //         <span className="text-title-small-emphasized">
            //           {grandSpecialTexture?.toLocaleString('vi-VN', {
            //             style: 'currency',
            //             currency: 'VND',
            //           })}
            //         </span>
            //       </>
            //     ) : (
            //       'Không dùng ván có sẵn'
            //     )}
            //   </div>
            // ),
          };
        }) ?? []
      );
    },
    [dataShipping]
  );

  // const renderGroundTripRow = useCallback(
  //   ({ exchangeRate }) => {
  //     return {
  //       name: 'Phí ground trip',
  //       tsln: (
  //         <Form.Item name={'groundTripProfit'} className="mb-0">
  //           <InputComponent type="number" />
  //         </Form.Item>
  //       ),
  //       costVND: calculateCostGroundTripNational().toLocaleString(),
  //       costForeignCurrency: (
  //         <Form.Item name={'groundTripForeign'} className="mb-0">
  //           <InputComponent type="number" />
  //         </Form.Item>
  //       ),
  //       saleVND: calculateSaleGroundTripNational().toLocaleString(),
  //       saleForeignCurrency: roundTo(
  //         calculateSaleGroundTripNational() / exchangeRate,
  //         2
  //       ).toLocaleString(),
  //     };
  //   },
  //   [calculateCostGroundTripNational, calculateSaleGroundTripNational]
  // );

  const renderTotalShippingTotalRow = useCallback(
    ({ exchangeRate }) => {
      return {
        name: (
          <span className="text-label-large text-black-high">
            Tổng phí ship
          </span>
        ),
        costVND: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(calculateCostNational(dataShipping), 0)?.toLocaleString()}
          </span>
        ),
        costForeignCurrency: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(
              calculateCostNational(dataShipping) / exchangeRate,
              2
            ).toLocaleString()}
          </span>
        ),
        saleVND: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(calculateSaleNational(dataShipping), 0)?.toLocaleString()}
          </span>
        ),
        saleForeignCurrency: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(
              calculateSaleNational(dataShipping) / exchangeRate,
              2
            ).toLocaleString()}
          </span>
        ),
        note: <div></div>,
      };
    },
    [calculateCostNational, calculateSaleNational, dataShipping]
  );
  const renderFinalTotalRow = useCallback(
    ({ exchangeRate }) => {
      return {
        name: (
          <span className="text-label-large">
            Tổng chi phí /
            <span className="text-label-large-emphasized text-black-high">
              Tổng giá bán
            </span>
          </span>
        ),
        costVND: calculateCostNational(data).toLocaleString(),
        costForeignCurrency: roundTo(
          calculateCostNational(data) / exchangeRate,
          2
        ).toLocaleString(),
        saleVND: (
          <span className="text-label-large-emphasized text-black-high">
            {calculateSaleNational(data).toLocaleString()}
          </span>
        ),
        saleForeignCurrency: (
          <span className="text-label-large-emphasized text-black-high">
            {roundTo(
              calculateSaleNational(data) / exchangeRate,
              2
            ).toLocaleString()}
          </span>
        ),
      };
    },
    [calculateCostNational, calculateSaleNational, data]
  );

  const renderGSTRow = useCallback(
    ({ exchangeRate }) => {
      return {
        name: <span className="">GST</span>,
        saleVND: (
          <span className="">
            {calculateGST(calculateSaleNational(data)).toLocaleString()}
          </span>
        ),
        saleForeignCurrency: (
          <span className="">
            {roundTo(
              calculateGST(calculateSaleNational(data)) / exchangeRate,
              2
            ).toLocaleString()}
          </span>
        ),
        note: (
          <Form.Item name={'GST'} label="GST" className="mb-0 max-w-[200px]">
            <InputComponent type="number" placeholder="% Thuế" suffix="%" />
          </Form.Item>
        ),
      };
    },
    [calculateGST, data, calculateSaleNational]
  );

  const renderFinalTotalAfterGSTRow = useCallback(
    ({ exchangeRate }) => {
      return {
        name: (
          <span className="text-title-small-emphasized text-black-high">
            Tổng giá bán sau thuế
          </span>
        ),
        saleVND: (
          <span className="text-title-small-emphasized text-black-high">
            {calculateSaleTotalAfterGST(
              calculateSaleNational(data)
            ).toLocaleString()}
          </span>
        ),
        saleForeignCurrency: (
          <span className="text-title-small-emphasized text-black-high">
            {roundTo(
              calculateSaleTotalAfterGST(calculateSaleNational(data)) /
                exchangeRate,
              2
            ).toLocaleString()}
          </span>
        ),
      };
    },
    [calculateSaleTotalAfterGST, calculateSaleNational, data]
  );

  const renderProfitRow = useCallback(
    ({ exchangeRate }) => {
      return {
        name: 'Lợi nhuận',
        saleVND: calculateProfit({
          costTotalNational: calculateCostNational(data),
          saleTotalAfterGST: calculateSaleTotalAfterGST(
            calculateSaleNational(data)
          ),
        }).toLocaleString(),
        saleForeignCurrency: roundTo(
          calculateProfit({
            costTotalNational: calculateCostNational(data),
            saleTotalAfterGST: calculateSaleTotalAfterGST(
              calculateSaleNational(data)
            ),
          }) / exchangeRate,
          2
        ).toLocaleString(),
        note: '= Tổng giá bán sau thuế - Tổng chi phí trong nước - Phí ship - Phí ground trip',
      };
    },
    [calculateProfit, calculateSaleTotalAfterGST, calculateCostNational, data]
  );

  const renderPercentProfitRow = useCallback(() => {
    return {
      name: (
        <span className="text-title-small-emphasized text-black-high">
          Tỷ suất lợi nhuận
        </span>
      ),
      saleForeignCurrency: (
        <span className="text-title-small-emphasized text-black-high">{`${calculatePercentProfit(data)}%`}</span>
      ),
      note: '= Lợi nhuận/ ( Tổng chi phí trong nước + Phí ship + Phí ground trip)',
    };
  }, [calculatePercentProfit, data]);

  const renderDataRow = useCallback(
    ({ exchangeRate }) => {
      return (
        dataNational?.map(item => {
          const costVND = roundTo(
            item.items?.reduce((prev, curr) => {
              return prev + curr.cost;
            }, 0),
            0
          );
          const saleVND = calculateSale({
            cost: costVND,
            name: item.name,
            baseFormulaWithProfit: item.baseFormulaWithProfit,
          });
          const isZeroed = zeroedRows.has(item.name);
          return {
            name: item.name,
            tsln: (
              <Form.Item name={item.name} className="mb-0">
                <InputComponent type="number" />
              </Form.Item>
            ),
            costVND: costVND?.toLocaleString(),
            costForeignCurrency: roundTo(
              costVND / exchangeRate,
              2
            ).toLocaleString(),
            saleVND: saleVND?.toLocaleString(),
            saleForeignCurrency: roundTo(
              saleVND / exchangeRate,
              2
            ).toLocaleString(),
            extra: (
              <ButtonIconGhostMedium
                icon={isZeroed ? <Icon.ICRotateLeft /> : <Icon.ICDelete />}
                onClick={() => onToggleZeroRow(item.name)}
              />
            ),
          };
        }) ?? []
      );
    },
    [dataNational, zeroedRows, onToggleZeroRow]
  );

  const dataForeign = useMemo(() => {
    const { exchangeRate, grand, grandCost, grandSpecialTexture } =
      getFormValues();

    return [
      ...renderDataRow({ exchangeRate }),
      renderTotalNationalRow({
        exchangeRate,
      }),
      // renderImportDutyRow({ exchangeRate }),
      ...renderShippingFeeRow({ exchangeRate }),
      renderTotalShippingTotalRow({ exchangeRate }),
      renderFinalTotalRow({ exchangeRate }),
      renderGSTRow({ exchangeRate }),
      renderFinalTotalAfterGSTRow({ exchangeRate }),
      renderProfitRow({ exchangeRate }),
      renderPercentProfitRow(),
    ];
  }, [
    getFormValues,
    renderDataRow,
    renderTotalNationalRow,
    // renderImportDutyRow,
    renderShippingFeeRow,
    // renderGroundTripRow,
    renderTotalShippingTotalRow,
    renderFinalTotalRow,
    renderGSTRow,
    renderFinalTotalAfterGSTRow,
    renderProfitRow,
    renderPercentProfitRow,
  ]);

  return (
    <div className="flex h-full w-full flex-col">
      {open && (
        <div className="flex items-center self-stretch px-24 py-48 justify-end gap-12 flex-col">
          <Form.Item name={'exchangeRate'} label="Tỷ giá ngoại tệ">
            <InputComponent
              placeholder="Nhập tỷ giá quy đổi"
              addonAfter="VNĐ"
            />
          </Form.Item>
          <Table
            dataSource={[...dataForeign]}
            pagination={false}
            // showHeader={false}
          >
            {RenderColumns()}
          </Table>
        </div>
      )}
    </div>
  );
};

const RenderColumns = () => {
  return (
    <>
      <Column
        title="Tên"
        dataIndex="name"
        key="name"
        width={200}
        render={value => (
          <div className="px-12 py-6 text-black-medium">{value}</div>
        )}
      />
      <Column
        title="TSLN"
        dataIndex="tsln"
        key="tsln"
        width={100}
        render={value => (
          <div className="px-12 py-6 text-black-medium">{value}</div>
        )}
      />
      <ColumnGroup title="Chi phí" width={600}>
        <Column
          title="VNĐ"
          dataIndex="costVND"
          key="costVND"
          className="!text-center"
          width={300}
          render={value => (
            <div className="px-12 py-6 text-black-medium">{value}</div>
          )}
        />
        <Column
          title="Ngoại tệ"
          dataIndex="costForeignCurrency"
          key="costForeignCurrency"
          className="!text-center"
          width={300}
          render={value => (
            <div className="px-12 py-6 text-black-medium">{value}</div>
          )}
        />
      </ColumnGroup>
      <ColumnGroup title="Giá bán" width={600}>
        <Column
          title="VNĐ"
          dataIndex="saleVND"
          key="saleVND"
          className="!text-center"
          width={300}
          render={value => (
            <div className="px-12 py-6 text-black-medium">{value}</div>
          )}
        />
        <Column
          title="Ngoại tệ"
          dataIndex="saleForeignCurrency"
          key="saleForeignCurrency"
          className="!text-center"
          width={300}
          render={value => (
            <div className="px-12 py-6 text-black-medium">{value}</div>
          )}
        />
      </ColumnGroup>
      <Column
        title=""
        dataIndex="extra"
        key="extra"
        width={100}
        render={value => (
          <div className="px-12 py-6 text-black-medium">{value}</div>
        )}
      />
      <Column
        title="Note"
        dataIndex="note"
        key="note"
        width={500}
        render={value => (
          <div className="px-12 py-6 text-black-medium">{value}</div>
        )}
      />
    </>
  );
};
